import { Controller } from "@hotwired/stimulus"
import ProgressBar from "../helpers/progress_bar"

const NS = ".customPackagingBundles"

// Mounted alongside the shared `datatable` controller on the same `.panel`: that one owns the
// DataTables glue including the debounced search, this one owns everything else this page needs.
export default class extends Controller {
  static get targets() {
    return ["table", "dateRangeInput", "creatorFilter", "statusFilter"]
  }

  static get values() {
    return { begin: String, end: String }
  }

  connect() {
    this.reinitTooltips = this.reinitTooltips.bind(this)
    $(this.tableTarget).on(`draw.dt${NS}`, this.reinitTooltips)

    this.initDateRangePicker()
    this.initFilterSelect(this.creatorFilterTarget, "Created By")
    this.initFilterSelect(this.statusFilterTarget, "Status")

    // select2 syncs the underlying `<select>` by calling jQuery's `.trigger("change")`, which
    // (unlike click/blur/submit) has no native invoker method, so jQuery never dispatches a real
    // DOM `change` event here -- a native `addEventListener("change", ...)` would never see it.
    $(this.element).on(`change${NS}`, ".datatable-moveable-filter", () => this.reload())
  }

  disconnect() {
    $(this.tableTarget).off(NS)

    const picker = $(this.dateRangeInputTarget).data("daterangepicker")
    if (picker) { picker.remove() }
    $(this.dateRangeInputTarget).off(NS)

    ;[this.creatorFilterTarget, this.statusFilterTarget].forEach((el) => {
      $(el).off(NS)
      if ($(el).data("select2")) { $(el).select2("destroy") }
    })

    $(this.element).off(NS)
  }

  initDateRangePicker() {
    $(this.dateRangeInputTarget).daterangepicker({
      buttonClasses: "btn btn-main btn-md",
      cancelButtonClasses: "btn-ghost",
      opens: "left",
      alwaysShowCalendars: true,
      showCustomRangeLabel: false,
      ranges: {
        "Today": [moment(), moment()],
        "Yesterday": [moment().subtract(1, "days"), moment().subtract(1, "days")],
        "Last 7 Days": [moment().subtract(6, "days"), moment()],
        "Last 30 Days": [moment().subtract(29, "days"), moment()],
        "This Month": [moment().startOf("month"), moment().endOf("month")],
        "Last Month": [moment().subtract(1, "month").startOf("month"), moment().subtract(1, "month").endOf("month")],
        "All Time": [moment(this.beginValue, "MM-DD-YYYY"), moment(this.endValue, "MM-DD-YYYY")],
      },
    }, (start, end) => this.applyRange(start, end))

    $(this.dateRangeInputTarget).
      on(`show.daterangepicker${NS}`, () => { $(".date-range-picker").addClass("is-open") }).
      on(`hide.daterangepicker${NS}`, () => { $(".date-range-picker").removeClass("is-open") })
  }

  applyRange(start, end) {
    const newRange = `${start.format("MMDDYYYY")}-${end.format("MMDDYYYY")}`
    this.reload({ range: newRange })
  }

  initFilterSelect(element, placeholder) {
    $(element).select2({ placeholder, width: "100%" })
    $(element).on(`select2:select${NS}`, (event) => this.markSelect2Active(event))
    $(element).on(`select2:unselect${NS}`, (event) => this.markSelect2Inactive(event))
  }

  markSelect2Active(event) {
    event.currentTarget.parentElement.querySelectorAll(".select2").forEach((el) => el.classList.add("select2-container--filled"))
  }

  markSelect2Inactive(event) {
    event.currentTarget.parentElement.querySelectorAll(".select2").forEach((el) => el.classList.remove("select2-container--filled"))
  }

  // Delegated from the mount: the dropdown and its sibling form both live inside the table's row
  // markup.
  onSendClick(event) {
    const link = event.target.closest(".send-cpb-link")
    if (!link) { return }

    event.preventDefault()

    // `form.submit()` bypasses the `submit` event; `requestSubmit()` fires one. jQuery's `.submit()`
    // fired jQuery-bound handlers and then called native `submit()`, so it never reached a native
    // `addEventListener("submit")` either -- `requestSubmit()` is therefore slightly *wider* than
    // what this replaced. Safe here because the only document-level submit listener,
    // single_action_guards.js, returns immediately unless the form carries [lt-single-submit], and
    // none of these views do. Use plain `.submit()` where that is not true (patterns 37).
    const form = link.parentElement.querySelector(":scope > form.edit_custom_packaging_bundle")
    if (form) { form.requestSubmit() }
  }

  reload(extraParams = {}) {
    ProgressBar.start()

    // The only two elements this page ever renders with `.datatable-moveable-filter` are the
    // creator and status filters -- both already declared targets.
    const filter = {}
    ;[this.creatorFilterTarget, this.statusFilterTarget].forEach((el) => {
      const key = el.getAttribute("name").replace(/(^.*\[|\].*$)/g, "")
      filter[key] = ($(el).select2("data") || []).map((option) => option.id)
    })
    extraParams["filter"] = filter

    this.eachIn(".datatable-moveable-text-filter, .datatable-text-filter", (el) => {
      extraParams["filter"][el.getAttribute("name").replace(/(^.*\[|\].*$)/g, "")] = el.value
    })

    const params = Object.assign(this.currentParams(), extraParams)

    const replacement = `${this.tableTarget.getAttribute("data-source").split("?")[0]}?${$.param(params)}`
    this.tableTarget.setAttribute("data-source", replacement)
    $(this.tableTarget).DataTable().ajax.url(replacement).load(() => ProgressBar.stop())
  }

  currentParams() {
    return this.urlParamsAsObject(this.tableTarget.getAttribute("data-source"))
  }

  // Bracket-notation query string into the nested object `$.param()` re-serializes; a
  // URLSearchParams rewrite would lose the nesting.
  urlParamsAsObject(dataSource) {
    const query = dataSource.substring(dataSource.indexOf("?") + 1)
    const regex = /([^&=]+)=?([^&]*)/g
    const decode = (str) => decodeURIComponent(str.replace(/\+/g, " "))

    const params = {}
    let match = regex.exec(query)
    while (match) {
      const k = decode(match[1])
      const v = decode(match[2])

      if (k.substring(k.length - 2) === "[]") {
        const key = k.substring(0, k.length - 2)
        ;(params[key] || (params[key] = [])).push(v)
      } else {
        params[k] = v
      }

      match = regex.exec(query)
    }

    const assignPath = (obj, keyPath, value) => {
      const lastKeyIndex = keyPath.length - 1
      let target = obj
      for (let i = 0; i < lastKeyIndex; i++) {
        const key = keyPath[i]
        if (!(key in target)) { target[key] = {} }
        target = target[key]
      }
      target[keyPath[lastKeyIndex]] = value
    }

    Object.keys(params).forEach((prop) => {
      const structure = prop.split("[")
      if (structure.length > 1) {
        const levels = structure.map((part) => part.replace(/[?[\]\\ ]/g, ""))
        assignPath(params, levels, params[prop])
        delete params[prop]
      }
    })

    return params
  }

  // initializers.js.erb only runs its global tooltip init on `turbo:load`, never on a DataTables
  // redraw, so without this the row-name tooltips would never come back after a search, filter or
  // page change.
  //
  // Fail-closed, as it has always been: `Promise.all` has no `.catch()`, so one broken image url
  // blocks the tooltip from initializing for the whole row.
  reinitTooltips() {
    const imageUrls = this.extractImageUrls()
    this.preloadTooltipImages(imageUrls).then(() => {
      this.eachIn("[data-toggle='tooltip']", (el) => { $(el).tooltip({ delay: { show: 50, hide: 50 } }) })
    })
  }

  extractImageUrls() {
    const imageUrls = []

    this.eachIn("[data-toggle='tooltip']", (el) => {
      const content = el.getAttribute("data-title")
      if (!content) { return }

      // `content` is CustomPackagingBundleDecorator#datatable_link_tooltip_content, which
      // interpolates the bundle's user-entered name/sku unescaped -- treat it as untrusted.
      // Proven exploitable via jQuery's `.html()` here even though the built `<div>` is never
      // inserted into the live document: a `<img onerror>` payload in the name still fires,
      // because HTML parsing eagerly starts the image fetch regardless of DOM connectedness.
      // DOMParser's document is inert for this too -- nothing in it fetches or executes.
      const doc = new DOMParser().parseFromString(content, "text/html")
      doc.querySelectorAll("img").forEach((img) => {
        const src = img.getAttribute("src")
        if (src) { imageUrls.push(src) }
      })
    })

    return imageUrls
  }

  preloadTooltipImages(imageUrls) {
    return Promise.all(imageUrls.map((url) => new Promise((resolve, reject) => {
      const img = new Image()
      img.src = url
      img.onload = resolve
      img.onerror = reject
    })))
  }

  eachIn(selector, callback) {
    this.element.querySelectorAll(selector).forEach(callback)
  }
}
